import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  UserResponse,
  AdminUserResponse,
  LookupAuthUserResponse,
  OrphanedAuthUser,
  ForceLinkUserResponse,
  AccountUserOnAccount,
} from "@adva/types";

// ─── Accounts ───────────────────────────────────────────────────────────────

export interface AccountWithCounts {
  id: string;
  name: string;
  slug: string;
  billing_contact_id: string | null;
  plan_type: string;
  plan_started_at: string | null;
  plan_expires_at: string | null;
  status: "active" | "suspended" | "cancelled";
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  business_count: number;
  user_count: number;
}

interface AccountsListResponse {
  accounts: AccountWithCounts[];
  total: number;
}

export interface AccountDetailResponse {
  account: {
    id: string;
    name: string;
    slug: string;
    status: string;
    plan_type: string;
    created_at: string;
    updated_at: string;
  };
  admin_existed?: boolean;
}

interface CreateAccountData {
  name: string;
  slug?: string;
  admin_email: string;
  admin_first_name?: string;
  admin_last_name?: string;
  first_business_name?: string;
  first_business_slug?: string;
  first_business_vertical?: string;
  force_link_existing_auth_user_id?: string;
}

export function useAllAccounts() {
  return useQuery({
    queryKey: ["admin", "accounts"],
    queryFn: () => api.get<AccountsListResponse>(`/core/accounts`),
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAccountData) =>
      api.post<AccountDetailResponse>(`/core/accounts`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "accounts"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "businesses"] });
    },
  });
}

// ─── Cross-account users ────────────────────────────────────────────────────

export type AdminUser = AdminUserResponse;

interface AdminUsersListResponse {
  users: AdminUser[];
  total: number;
}

interface AdminUsersParams {
  q?: string;
  account_id?: string;
  status?: string;
  role?: string;
  limit?: number;
  offset?: number;
}

function buildQuery(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "" && v !== "all") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function useAllUsers(params: AdminUsersParams = {}) {
  return useQuery({
    queryKey: ["admin", "users", params],
    queryFn: () =>
      api.get<AdminUsersListResponse>(
        `/core/admin/users${buildQuery({ limit: 50, ...params })}`,
      ),
  });
}

interface AdminInviteUserData {
  email: string;
  first_name?: string;
  last_name?: string;
  account_role: string;
  business_ids?: string[];
  business_role?: string;
  end_user_role?: string | null;
  force_link_existing_auth_user_id?: string;
}

interface AdminInviteResponse {
  user: UserResponse;
}

export function useAdminInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      data,
    }: {
      accountId: string;
      data: AdminInviteUserData;
    }) =>
      api.post<AdminInviteResponse>(
        `/core/admin/accounts/${accountId}/invite`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
  });
}

// ─── Auth user lookup (ADV-387) ─────────────────────────────────────────────

// Keyed on the trimmed, lowercased email so identical queries hit the cache
// even if the user has retyped the same address with different casing.
export function useLookupAuthUserByEmail(email: string) {
  const normalized = email.trim().toLowerCase();
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);

  return useQuery({
    queryKey: ["admin", "auth-user-lookup", normalized],
    queryFn: () =>
      api.get<LookupAuthUserResponse>(
        `/core/admin/users/lookup-by-email?email=${encodeURIComponent(normalized)}`,
      ),
    enabled: isValid,
    // Inputs rarely change — keep stale results while re-validating so the
    // banner doesn't flicker on unrelated re-renders.
    staleTime: 30_000,
  });
}

// ─── Orphaned users + recovery actions (ADV-384) ────────────────────────────

export type { OrphanedAuthUser };

interface OrphanedUsersResponse {
  users: OrphanedAuthUser[];
  total: number;
}

export function useOrphanedUsers() {
  return useQuery({
    queryKey: ["admin", "users", "orphaned"],
    queryFn: () => api.get<OrphanedUsersResponse>(`/core/admin/users/orphaned`),
  });
}

interface ResendInviteResponse {
  message: string;
  email: string;
}

function invalidateAdminUsers(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
  queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
}

export function useAdminResendInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<ResendInviteResponse>(
        `/core/admin/users/${accountUserId}/resend-invite`,
        undefined,
      ),
    onSuccess: () => invalidateAdminUsers(queryClient),
  });
}

type ForceLinkResponse = ForceLinkUserResponse;

export function useAdminForceLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<ForceLinkResponse>(
        `/core/admin/users/${accountUserId}/force-link`,
        undefined,
      ),
    onSuccess: () => invalidateAdminUsers(queryClient),
  });
}

// ─── Per-account users (ADV-389) ────────────────────────────────────────────

export type { AccountUserOnAccount };

interface AccountUsersListResponse {
  users: AccountUserOnAccount[];
  total: number;
}

export function useAccountUsers(accountId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "accounts", accountId, "users"],
    queryFn: () =>
      api.get<AccountUsersListResponse>(
        `/core/admin/accounts/${accountId}/users`,
      ),
    enabled: !!accountId,
  });
}

export function useAdminRemoveUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<{ message: string }>(
        `/core/admin/users/${accountUserId}/remove`,
        undefined,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}

// ─── ADV-623: User management panel ─────────────────────────────────────────

interface AdminUserDetails extends AdminUser {
  auth_user_id: string | null;
  email_confirmed_at: string | null;
  banned: boolean;
  business_assignments: {
    business_id: string;
    business_name: string;
    role: string | null;
  }[];
}

interface ResetPasswordResponse {
  message: string;
  link_sent: boolean;
}

export function useAdminResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<ResetPasswordResponse>(
        `/core/admin/users/${accountUserId}/reset-password`,
        undefined,
      ),
    onSuccess: () => invalidateAdminUsers(queryClient),
  });
}

interface SuspendResponse {
  message: string;
  suspended: boolean;
}

export function useAdminSuspendUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.post<SuspendResponse>(
        `/core/admin/users/${accountUserId}/suspend`,
        undefined,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}

interface UpdateRoleInput {
  account_role?: string;
  business_assignments?: {
    business_id: string;
    role: string;
    end_user_role?: string | null;
  }[];
}

export function useAdminUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: UpdateRoleInput & { userId: string }) =>
      api.patch<AdminUserDetails>(`/core/admin/users/${userId}/role`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}

export function useAdminDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountUserId: string) =>
      api.delete<{ message: string }>(`/core/admin/users/${accountUserId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}
